/**
 * The sample SLA clock.
 *
 * Pure, and takes `now` as an argument rather than reading the clock, for the
 * usual reason plus a sharper one: a countdown is the one thing in the app
 * whose interesting states — about to breach, just breached — are impossible
 * to reach by clicking around. If the boundaries are not testable they are not
 * tested, and a badge that says "1 day remaining" on an overdue order is worse
 * than no badge at all.
 *
 * The clock starts when the supplier CONFIRMS, not when the PO is issued.
 * Counting from issue would charge a factory for the days TAP took to reach
 * them, which is neither fair nor useful as a measure of the factory.
 */

/** Now, as an ISO instant. One call site for the clock keeps it swappable. */
export function todayNow(): string {
  return new Date().toISOString();
}

export type SlaState = 'not_started' | 'on_track' | 'due_soon' | 'overdue' | 'met' | 'missed';

export interface Sla {
  state: SlaState;
  /** Whole hours left. Negative once overdue. Zero before the clock starts. */
  hoursRemaining: number;
  /** Whole days left, rounded UP: 25 hours is "2 days", not "1". */
  daysRemaining: number;
  /** Ready to render, emoji included. */
  label: string;
}

const HOUR_MS = 3_600_000;

/** Under this many hours left and the badge turns amber. */
const DUE_SOON_HOURS = 24;

/**
 * Where an order stands against its sample deadline.
 *
 * `startedAt` null means the supplier has not confirmed yet, which is NOT the
 * same as being on time with the full window left — nothing is owed until
 * somebody has accepted the work.
 *
 * ── settledAt: the clock has to stop ───────────────────────────────────────
 *
 * Without it, this counted forever. Ced, 15 September, on two orders that were
 * already In Production: "Could you clarify what this Sample SLA tracking is
 * specifically measuring, and what actions/triggers clear or reset that
 * status?" The answer was nothing did, so #1043 and #1042 were showing "SLA
 * overdue by 5 days" and "by 10 days" for a sample phase that had finished.
 *
 * Nothing was wrong with the arithmetic. The clock simply had no end, and a
 * countdown with no end is a stopwatch.
 *
 * Pass the moment the phase ended and the result freezes against THAT instead
 * of against now, so the badge becomes a record rather than an alarm. For a
 * sample window the end is `production_clock_started_at`: 20261109000100 sets
 * it on the move into In Production and says why in its own comment, "the
 * production clock starts when the samples are approved, which IS the move
 * into In Production". So the end already existed as data. Nobody had joined
 * the two clocks up.
 */
export function slaRemaining(
  startedAt: string | null,
  dueDays: number,
  now: string,
  /** When this phase ended. Null while it is still running. */
  settledAt: string | null = null,
): Sla {
  if (startedAt === null || startedAt === '') {
    return {
      state: 'not_started',
      hoursRemaining: 0,
      daysRemaining: 0,
      label: 'Not started',
    };
  }

  const started = new Date(startedAt).getTime();
  // A settled phase is measured to its end, never to today. This is the whole
  // fix: same arithmetic, different second operand.
  const current = new Date(settledAt ?? now).getTime();
  if (Number.isNaN(started) || Number.isNaN(current)) {
    return { state: 'not_started', hoursRemaining: 0, daysRemaining: 0, label: 'Not started' };
  }

  if (settledAt !== null && settledAt !== '') {
    const overrunMs = current - (started + dueDays * 24 * HOUR_MS);
    if (overrunMs <= 0) {
      return {
        state: 'met',
        hoursRemaining: Math.trunc(-overrunMs / HOUR_MS),
        daysRemaining: Math.trunc(-overrunMs / (24 * HOUR_MS)),
        label: 'Sample SLA met',
      };
    }
    // Same rounding as the live overdue branch, so an order that was two days
    // late while running still reads "2 days" once it settles. A badge that
    // changed its number at the moment of approval would look like a bug.
    const lateBy = Math.max(1, Math.ceil(overrunMs / (24 * HOUR_MS)));
    return {
      state: 'missed',
      hoursRemaining: -Math.trunc(overrunMs / HOUR_MS),
      daysRemaining: -Math.trunc(overrunMs / (24 * HOUR_MS)),
      label: `Missed by ${String(lateBy)} ${lateBy === 1 ? 'day' : 'days'}`,
    };
  }

  const deadline = started + dueDays * 24 * HOUR_MS;
  const remainingMs = deadline - current;

  // Overdue is decided on the RAW milliseconds, never on the rounded hours.
  // Math.trunc(-0.016) is -0, which is not < 0 — so an order one minute past
  // its deadline stayed amber for a further 59 minutes. Found by asserting
  // both sides of the boundary rather than the middle.
  if (remainingMs < 0) {
    const hoursRemaining = Math.floor(remainingMs / HOUR_MS);
    const overdueBy = Math.max(1, Math.ceil(Math.abs(remainingMs) / (24 * HOUR_MS)));
    return {
      state: 'overdue',
      hoursRemaining,
      daysRemaining: Math.floor(hoursRemaining / 24),
      label: `🔴 SLA overdue by ${String(overdueBy)} ${overdueBy === 1 ? 'day' : 'days'}`,
    };
  }

  // Truncated toward zero on purpose. With 90 minutes left this reads 1 hour,
  // never 2: a countdown should not round in the optimistic direction.
  const hoursRemaining = Math.trunc(remainingMs / HOUR_MS);

  if (hoursRemaining < DUE_SOON_HOURS) {
    return {
      state: 'due_soon',
      hoursRemaining,
      daysRemaining: hoursRemaining === 0 ? 0 : 1,
      label: `🟡 Under 24 hours (${String(hoursRemaining)}h)`,
    };
  }

  const daysRemaining = Math.ceil(hoursRemaining / 24);
  return {
    state: 'on_track',
    hoursRemaining,
    daysRemaining,
    label: `🟢 ${String(daysRemaining)} ${daysRemaining === 1 ? 'day' : 'days'} remaining`,
  };
}

/**
 * The design-system tone for a badge showing this state.
 *
 * `met` and `missed` are both neutral, and the missed one deliberately so.
 * A settled result is a record, not a thing to act on, and red is the app's
 * word for "do something". Kyu, choosing how a finished window should read:
 * freeze the outcome quietly. The number is still there for anyone judging a
 * factory at renewal; it just stops shouting on a dashboard every morning.
 */
export function slaTone(state: SlaState): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (state) {
    case 'on_track':
      return 'success';
    case 'due_soon':
      return 'warning';
    case 'overdue':
      return 'danger';
    default:
      return 'neutral';
  }
}
